import * as React from "react";
import { useMappedState } from "redux-react-hook";
import { useHistory } from "react-router-dom";

import { TabbedNavigation } from "src/library/components/TabbedNavigation";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { PageProps } from "src/pages/Router/routes";
import ViewAdminSupportTicketsTab from "src/modules/support/components/AdminSupportTabs/ViewAdminSupportTicketsTab";
import CreditRequestsNotesSupportTab from "src/modules/credit-request/components/CreditRequestsNotesSupportTab";
import { IStore } from "src/core/shared/store";
import { LikeToDo } from "src/library/components/LikeToDo";
import { Button } from "src/library/components/Button";
import { useDrop } from "src/core/drops/hooks";

/**
 * Main "Support" View for admins
 * Contains 3 tabs
 * - Support Tickets
 * - Credit Requests
 * - Credit Notes
 */
const ViewDropSupportPageAdmin: React.FC<PageProps<unknown>> = ({ title, ...props }) => {
  const history = useHistory();
  const { installedModules } = useMappedState((store: IStore) => ({ installedModules: store.global.settings?.installedModules }));
  const dropId = props.match?.params.dropId ? parseInt(props.match?.params.dropId) : undefined;

  // used to set current drop in state for navigation
  useDrop(dropId);

  const tabs = React.useMemo(() => {
    const supportTabs = [
      {
        title: "Support Tickets",
        content: () => (
          <ViewAdminSupportTicketsTab
            history={props.history}
            terminology={props.terminology}
            dropId={dropId}
          />
        )
      }
    ];

    if (installedModules?.includes("credit-request")) {
      supportTabs.push({
        title: "Credit Requests",
        content: () => (
          <CreditRequestsNotesSupportTab
            history={props.history}
            isCreditRequests
            terminology={props.terminology}
            dropId={dropId}
          />
        )
      });

      supportTabs.push({
        title: "Credit Notes",
        content: () => (
          <CreditRequestsNotesSupportTab
            history={props.history}
            terminology={props.terminology}
            dropId={dropId}
          />
        )
      });
    }

    return supportTabs;
  }, [
    dropId,
    installedModules,
    props.history,
    props.terminology
  ]);

  const LikeToDoActions: React.ReactElement[] = React.useMemo(() => {
    const actions: React.ReactElement[] = [
      <Button
        key="create-support-ticket"
        onClick={() => history.push("tickets/raise-ticket", {
          cleanStart: true,
          context: "drop"
        })}
      >
        Create New Support Ticket
      </Button>
    ];

    return actions;
  }, [ history ]);

  return (
    <Layout
      title={title}
      {...props}
    >
      <MainContentWrapper
        fill
        Header={(
          <LikeToDo
            copy="What would you like to do?"
            buttons={LikeToDoActions}
          />
        )}
      >
        <TabbedNavigation
          defaultTab="support"
          className="tab-navigator"
          tabs={tabs}
        />
      </MainContentWrapper>
    </Layout>
  );
};

export default ViewDropSupportPageAdmin;
